import React from 'react'
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from 'reactstrap'
import { criarAditivo, parcelaTravada } from './aditarContrForn'

const moeda = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const dataCurta = (valor) => {
  const texto = String(valor || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return ''
  const [ano, mes, dia] = texto.split('-')
  return `${dia}/${mes}/${ano}`
}

export default function ModalAditarContrForn({ contrato, onClose, onSaved, user }) {
  const [flags, setFlags] = React.useState({ prazo: false, valor: false, objeto: false })
  const [dtVigFim, setDtVigFim] = React.useState('')
  const [vlrGlobal, setVlrGlobal] = React.useState('')
  const [objeto, setObjeto] = React.useState('')
  const [motivo, setMotivo] = React.useState('')
  const [parcelas, setParcelas] = React.useState([])
  const [carregando, setCarregando] = React.useState(false)
  const [salvando, setSalvando] = React.useState(false)

  React.useEffect(() => {
    if (!contrato) return undefined
    let ativo = true
    setFlags({ prazo: false, valor: false, objeto: false })
    setDtVigFim(String(contrato.contrForn_dtVigFim || '').slice(0, 10))
    setVlrGlobal(contrato.contrForn_vlrGlobal ?? '')
    setObjeto(contrato.contrForn_obj || '')
    setMotivo('')
    setParcelas([])
    setCarregando(true)

    import('./contratosTabHelpers').then(async ({ fetchContrFornEventosWithBordero }) => {
      const oikosApi = (await import('../../services/oikos')).default
      try {
        const eventos = await fetchContrFornEventosWithBordero(oikosApi, contrato.contrForn_id)
        if (!ativo) return
        setParcelas((eventos || []).map((evento) => ({
          ...evento,
          contrFornEvento_dtPrev: String(evento.contrFornEvento_dtPrev || '').slice(0, 10),
        })))
      } catch (error) {
        console.error(error)
      } finally {
        if (ativo) setCarregando(false)
      }
    })

    return () => { ativo = false }
  }, [contrato])

  const atualizarParcela = (indice, campo, valor) => {
    setParcelas((lista) => lista.map((parcela, i) => (i === indice ? { ...parcela, [campo]: valor } : parcela)))
  }

  const removerParcela = (indice) => {
    setParcelas((lista) => lista.filter((_, i) => i !== indice))
  }

  const adicionarParcela = () => {
    const proximo = parcelas.reduce((max, parcela) => Math.max(max, Number(parcela.contrFornEvento_num) || 0), 0) + 1
    setParcelas((lista) => [...lista, {
      contrFornEvento_num: proximo,
      contrFornEvento_desc: `Parcela ${proximo}`,
      contrFornEvento_dtPrev: '',
      contrFornEvento_vlr: 0,
    }])
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      const ok = await criarAditivo({
        contrato,
        flags,
        motivo,
        dtVigFim,
        vlrGlobal,
        objeto,
        parcelas,
        user,
      })
      if (ok) {
        onClose()
        if (onSaved) await onSaved()
      }
    } finally {
      setSalvando(false)
    }
  }

  const soma = parcelas.reduce((acc, parcela) => acc + (Number(parcela.contrFornEvento_vlr) || 0), 0)
  const aberto = Boolean(contrato)

  return (
    <Modal isOpen={aberto} toggle={onClose} size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>
        Aditar {contrato?.contrForn_num}
      </ModalHeader>
      <ModalBody>
        {carregando ? <Spinner size="sm" /> : (
          <>
            <p className="mb-2">
              {contrato?.contratado_nomePj || contrato?.contratado_nomePf || 'Fornecedor'}
              {' · '}
              vigência {dataCurta(contrato?.contrForn_dtVigIni)} a {dataCurta(contrato?.contrForn_dtVigFim)}
              {' · '}
              {moeda(contrato?.contrForn_vlrGlobal)}
            </p>
            <p className="text-muted">O contrato assinado permanece como está até este aditivo ser aprovado e o PDF assinado ser anexado.</p>
            <FormGroup>
              <Label>O que muda</Label>
              <div>
                {['prazo', 'valor', 'objeto'].map((tipo) => (
                  <Label key={tipo} check className="mr-3">
                    <Input
                      type="checkbox"
                      checked={flags[tipo]}
                      onChange={(event) => setFlags((atual) => ({ ...atual, [tipo]: event.target.checked }))}
                    />
                    {' '}
                    {tipo === 'prazo' ? 'Prazo' : tipo === 'valor' ? 'Valor' : 'Objeto'}
                  </Label>
                ))}
              </div>
            </FormGroup>
            {flags.prazo && (
              <FormGroup>
                <Label>Nova data final</Label>
                <Input type="date" value={dtVigFim} onChange={(event) => setDtVigFim(event.target.value)} />
              </FormGroup>
            )}
            {flags.valor && (
              <FormGroup>
                <Label>Novo valor global</Label>
                <Input type="number" min="0" step="0.01" value={vlrGlobal} onChange={(event) => setVlrGlobal(event.target.value)} />
                <small className="text-muted">Soma das parcelas: {moeda(soma)}</small>
              </FormGroup>
            )}
            {flags.objeto && (
              <FormGroup>
                <Label>Novo objeto</Label>
                <Input type="textarea" rows={3} value={objeto} onChange={(event) => setObjeto(event.target.value)} />
              </FormGroup>
            )}
            {flags.valor && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">Parcelas</Label>
                  <Button size="sm" color="link" onClick={adicionarParcela}>Adicionar parcela</Button>
                </div>
                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th>Data prevista</th>
                      <th>Valor</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {parcelas.map((parcela, indice) => {
                      const travada = parcelaTravada(parcela)
                      return (
                        <tr key={parcela.contrFornEvento_id || `nova-${indice}`}>
                          <td>
                            <Input
                              bsSize="sm"
                              disabled={travada}
                              value={parcela.contrFornEvento_desc || ''}
                              onChange={(event) => atualizarParcela(indice, 'contrFornEvento_desc', event.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              bsSize="sm"
                              type="date"
                              disabled={travada}
                              value={String(parcela.contrFornEvento_dtPrev || '').slice(0, 10)}
                              onChange={(event) => atualizarParcela(indice, 'contrFornEvento_dtPrev', event.target.value)}
                            />
                          </td>
                          <td>
                            <Input
                              bsSize="sm"
                              type="number"
                              min="0"
                              step="0.01"
                              disabled={travada}
                              value={parcela.contrFornEvento_vlr ?? ''}
                              onChange={(event) => atualizarParcela(indice, 'contrFornEvento_vlr', event.target.value)}
                            />
                          </td>
                          <td>
                            {travada ? 'Lançada' : (
                              <Button size="sm" color="link" onClick={() => removerParcela(indice)}>Remover</Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </>
            )}
            <FormGroup>
              <Label>Motivo</Label>
              <Input type="textarea" rows={2} value={motivo} onChange={(event) => setMotivo(event.target.value)} />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={salvando}>Cancelar</Button>
        <Button color="primary" onClick={salvar} disabled={salvando || carregando}>
          {salvando ? <Spinner size="sm" /> : 'Criar aditivo'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

